import React from "react";
import { ResourceDWR } from "../../../dto/ResourceDWR";
import { useLanguage } from "../../Language";
import { Button, SxProps } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { ResourceView } from "../../../dto/ResourceView";
import { useDownloadableSource } from "./DownloadableSource";
import { AccountHolder } from "../../Account";
import { useToast } from "@tlc/ui/toast";
import { useAnalytics } from "../../../analytics/useGoogleAnalytics";
import { useLoginContext } from "../../LoginContext";
import { useDownloadableDialogs } from "./downloadable/DownloadableDialogs";

interface DownloadButtonProps {
  resource: ResourceDWR | ResourceView;
  sx?: SxProps;
}

export const DownloadButton = ({ resource, sx }: DownloadButtonProps) => {
  const { translate } = useLanguage();
  const { showToast } = useToast();
  const { promptIfNotLoggedIn } = useLoginContext();
  const { openCheckoutDialog, openPlaceHoldDialog } = useDownloadableDialogs();
  const { trackDownloadableClick } = useAnalytics();
  const { source, loading, hasDownloadbleCheckouts } =
    useDownloadableSource(resource);

  if (!resource.downloadable) {
    console.warn("Incorrect use of DownloadButton");
    return null;
  }

  const onClickFullEIntegration = () => {
    // TODO replace with a LoginContext hook
    promptIfNotLoggedIn(() => {
      if (source.availability) {
        // checkout
        openCheckoutDialog({
          resource,
          source,
        });
        trackDownloadableClick("downloadable_checkout", resource, source);
      } else {
        // place hold
        openPlaceHoldDialog({
          resource,
          source,
        });
        trackDownloadableClick("downloadable_place_hold", resource, source);
      }
    });
  };

  // FIXME handle availability has changed (recheck source (cache bust))
  // FIXME handle bad borrower
  // TODO split Downloadable that supports 3rd party checkouts from the rest of DownloadButton
  // TODO handle Holds use case once refactored

  if (AccountHolder.isAdmin) {
    return null;
  }
  if (hasDownloadbleCheckouts) {
    // Downloadable that supports 3rd party checkouts
    return (
      <>
        <Button
          sx={{ ...sx }}
          variant="contained"
          loading={loading}
          startIcon={source?.availability ? <DownloadIcon /> : <ScheduleIcon />}
          onClick={onClickFullEIntegration}
        >
          {source?.availability
            ? translate("download")
            : translate("placeRequest")}
        </Button>
      </>
    );
  }

  // Regular Downloadable - treated as an external link
  return (
    <Button
      sx={{ ...sx, whiteSpace: "nowrap" }}
      variant="contained"
      href={resource.link}
      loading={loading}
      startIcon={source?.availability ? <DownloadIcon /> : <OpenInNewIcon />}
      target={"_blank"}
      onClick={() =>
        trackDownloadableClick(
          "downloadable_external_link_click",
          resource,
          source,
        )
      }
    >
      {source?.availability
        ? translate("download")
        : translate("checkDigitalLocations")}
    </Button>
  );
};
